import setup from '../assets/data/setup.json';
import { toFirstPerson } from './letterUtils';
import type { JobDetails, ParsedJob, ProfileMatch } from './types';

function scoreText(text: string, keywords: string[]): number {
  const lower = text.toLowerCase();
  return keywords.reduce((score, kw) => (lower.includes(kw.toLowerCase()) ? score + 1 : score), 0);
}

export function matchProfileToJob(parsed: ParsedJob, job: JobDetails): ProfileMatch {
  const keywords = parsed.keywords;
  const jdText = job.jobDescription.toLowerCase();

  const scoredExperiences = setup.experiences.map((exp) => {
    const text = [
      exp.subtitle,
      exp.highlight,
      exp.role,
      ...(exp.technologies || []),
      ...exp.bulletPoints,
      ...(exp.businessImpact || []),
      ...((exp as { majorContributions?: string[] }).majorContributions || []),
    ].join(' ');
    let score = scoreText(text, keywords) + scoreText(text, [job.role]);

    const roleLower = job.role.toLowerCase();
    if (/front[\s-]?end|ui|react/.test(roleLower)) {
      score += scoreText(text, ['react', 'next.js', 'typescript', 'tailwind', 'frontend']);
    }
    if (/full[\s-]?stack/.test(roleLower)) {
      score += scoreText(text, ['react', 'node', 'typescript', 'full-stack']);
    }

    return { exp, score };
  });

  const topExp = scoredExperiences.sort((a, b) => b.score - a.score)[0]?.exp;

  const scoredProjects = setup.projects.map((project) => {
    const text = [
      project.title,
      project.description,
      project.businessImpact,
      ...(project.technologies || []),
      ...((project as { challenges?: string[] }).challenges || []),
    ].join(' ');
    return { project, score: scoreText(text, keywords) };
  });

  const topProject = scoredProjects.sort((a, b) => b.score - a.score)[0]?.project;

  const allSkills = Object.values(setup.skills).flat();
  const matchedSkills = allSkills.filter(
    (skill) =>
      keywords.some((kw) => skill.toLowerCase().includes(kw.toLowerCase()) || kw.toLowerCase().includes(skill.toLowerCase())) ||
      jdText.includes(skill.toLowerCase())
  ).slice(0, 6);

  const matchedIntegrations = setup.integrations
    .map((i) => i.name)
    .filter((name) => jdText.includes(name.toLowerCase()) || keywords.some((k) => name.toLowerCase().includes(k)))
    .slice(0, 4);

  const experienceHighlight = topExp
    ? (() => {
        const bestBullet =
          topExp.bulletPoints.length > 1
            ? [...topExp.bulletPoints]
                .map((bp) => ({ bp, score: scoreText(bp, keywords) + scoreText(bp, [job.role]) }))
                .sort((a, b) => b.score - a.score)[0]?.bp
            : topExp.bulletPoints[0];
        const achievement = bestBullet
          ? toFirstPerson(bestBullet)
          : `I led work on ${topExp.highlight.toLowerCase()}`;
        return `At ${topExp.subtitle}, ${achievement}.`;
      })()
    : `Over the past ${setup.stats.yearsExperience}+ years, I've shipped production software for US companies across enterprise SaaS, ERP, and automation.`;

  const projectSource = topProject
    ? (topProject as { solution?: string }).solution || topProject.description
    : '';

  const projectHighlight = topProject
    ? `${topProject.title}: ${toFirstPerson(projectSource)}.`
    : setup.profile.heroOneLiner || setup.profile.tagline;

  const matchedImpact =
    topExp?.businessImpact?.[0] ||
    (topProject as { businessImpactItems?: string[] })?.businessImpactItems?.[0] ||
    topProject?.businessImpact;

  const relevanceScore =
    scoredExperiences[0]?.score +
    scoredProjects[0]?.score +
    matchedSkills.length +
    matchedIntegrations.length;

  return {
    experienceHighlight,
    projectHighlight,
    matchedSkills,
    matchedIntegrations,
    matchedImpact: typeof matchedImpact === 'string' ? matchedImpact : undefined,
    relevanceScore,
  };
}
